import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import MaterialTable, { MTableToolbar } from "material-table";
import axios from "axios";
import { ToastContainer, toast } from "react-toastify";
import { confirmAlert } from "react-confirm-alert";
import SlidingPane from "react-sliding-pane";
import "react-sliding-pane/dist/react-sliding-pane.css";
import Helper from "../../Helper";
import { API } from "../../backend";
import authHeader from "../../services/auth-header";
import { getTrans as t } from "../../Localization/translations";

function Transactions() {
    const tableRef = React.createRef();
    const navigate = useNavigate();

    const [data, setData] = useState([]);
    const [filteredData, setFilteredData] = useState([]);
    const [loading, setLoading] = useState(true);

    // filter states
    const savedFilter = localStorage.getItem("transactionTypeFilter") || "Expense";
    const [filterType, setFilterType] = useState(savedFilter);
    const [paneState, setPaneState] = useState({ isPaneOpen: false });

    useEffect(() => {
        loadData();
    }, []);


    // =========================
    // LOAD + MERGE DATA
    // =========================

    const loadData = async () => {
        try {
            setLoading(true);

            const [expenseRes, manualRes] = await Promise.all([
                axios.get(API + "expenses/", { headers: authHeader() }),
                axios.get(API + "manualaccounting/", { headers: authHeader() }),
            ]);

            let expenses =
                expenseRes.data.data ||
                expenseRes.data.results ||
                expenseRes.data ||
                [];

            let manuals =
                manualRes.data.data ||
                manualRes.data.results ||
                manualRes.data ||
                [];

            if (!Array.isArray(expenses)) expenses = [];
            if (!Array.isArray(manuals)) manuals = [];

            const merged = [
                ...expenses.map((e) => ({
                    id: e.id,
                    transaction_no: e.expense_number,
                    date: e.expense_date,
                    status: e.status || "-",
                    request: e.approval_status || "-",
                    type: "Expense",
                    customer: e.customer_name || "-",
                    amount: e.amount || 0,
                    fee_type:
                        e.allocations && e.allocations.length > 0
                            ? e.allocations[0].fee_type
                            : "-",
                    raw: e,
                })),
                ...manuals.map((m) => ({
                    id: m.id,
                    transaction_no: m.accounting_number,
                    date: m.entry_date,
                    status: m.status || "-",
                    request: m.approval_status || "-",
                    type: "Manual",
                    customer:
                        m.allocations && m.allocations.length > 0
                            ? m.allocations[0].customer_name
                            : "-",
                    amount: m.amount || 0,
                    fee_type: "-", // ❌ not available in manual
                    raw: m,
                })),
            ].sort((a, b) => new Date(b.date) - new Date(a.date));


            setData(merged);
            const currentFilter = localStorage.getItem("transactionTypeFilter") || "Expense";
            const defaultFiltered = currentFilter
                ? merged.filter(d => d.type === currentFilter)
                : merged;
            setFilteredData(defaultFiltered);
            setFilterType(currentFilter);
            setLoading(false);
        } catch (err) {
            console.error("Transaction load error:", err);
            setLoading(false);
        }
    };

    const applyFilter = () => {
        localStorage.setItem("transactionTypeFilter", filterType);
        if (!filterType) {
            setFilteredData(data);
        } else {
            setFilteredData(data.filter(d => d.type === filterType));
        }
        setPaneState({ isPaneOpen: false });
    };

    const handleClose = () => {
        setFilterType(localStorage.getItem("transactionTypeFilter") || "Expense");
        setPaneState({ isPaneOpen: false });
    };

    const resetFilter = () => {
        const expenseOnly = data.filter(d => d.type === "Expense");

        setFilterType("Expense");
        setFilteredData(expenseOnly);
    };


    const goView = (row) => {
        if (row.type === "Expense")
            navigate("/expenses/view", { state: row.raw.id });
        else
            navigate("/viewmanualaccounting", { state: row.raw.id });
    };

    const handleEdit = (row) => {
        if (row.type === "Expense")
            navigate(`/expenses/edit/${row.raw.id}`);
        else
            navigate("/editmanualaccounting", { state: { id: row.raw.id } });
    };



    const handleDelete = (row) => {
        const endpoint =
            row.type === "Expense"
                ? `expenses/${row.id}/`
                : `manualaccounting/${row.id}/`;

        confirmAlert({
            message: "Are you sure you want to delete this transaction?",
            buttons: [
                {
                    label: "Yes",
                    onClick: async () => {
                        try {
                            await axios.delete(API + endpoint, {
                                headers: authHeader(),
                            });

                            await loadData(); // 🔥 Proper refresh

                            toast.success("Deleted successfully", {
                                autoClose: 1000,
                            });
                        } catch (err) {
                            toast.error("Delete failed");
                        }
                    },
                },
                { label: "No" },
            ],
        });
    };


    return (
        <div className="page-wrapper">
            <ToastContainer />

            <div className="content container-fluid">
                <div className="row show_contract show_invoice">
                    <div className="">
                        <div className="col-md-12">
                            <MaterialTable
                                tableRef={tableRef}
                                isLoading={loading}
                                style={{
                                    backgroundColor: "var(--bs-table-bg)",
                                    borderBottomWidth: "1px",
                                    boxShadow:
                                        "inset 0 0 0 9999px var(--bs-table-accent-bg)",
                                }}
                                title={
                                    <h5 className="mb-0">
                                        {t("Transactions")}
                                    </h5>
                                }
                                columns={[
                                    {
                                        title: "Transaction No",
                                        field: "transaction_no",
                                        headerStyle: { fontWeight: "bold", textAlign: "left" },
                                        cellStyle: { textAlign: "left", fontSize: 12 },
                                        render: (row) => (
                                            <>
                                                <div className="d-xl-none tc d-md-none d-lg-none">
                                                    Transaction No
                                                </div>
                                                <div
                                                    className="hyperlink"
                                                    onClick={() => goView(row)}
                                                >
                                                    {row.transaction_no}
                                                </div>
                                            </>
                                        ),
                                    },
                                    {
                                        title: "Transaction Type",
                                        field: "type",
                                        headerStyle: { fontWeight: "bold", textAlign: "left" },
                                        cellStyle: { textAlign: "left", fontSize: 12 },
                                        render: (row) => (
                                            <>
                                                <div className="d-xl-none tc d-md-none d-lg-none">
                                                    Transaction Type
                                                </div>
                                                <div>{row.type}</div>
                                            </>
                                        ),
                                    },
                                    {
                                        title: t("Date"),
                                        field: "date",
                                        headerStyle: { fontWeight: "bold", textAlign: "left" },
                                        cellStyle: { textAlign: "left", fontSize: 12 },
                                        render: (row) => (
                                            <>
                                                <div className="d-xl-none tc d-md-none d-lg-none">
                                                    Date
                                                </div>
                                                <div>
                                                    {Helper.DateNormal
                                                        ? Helper.DateNormal(row.date)
                                                        : row.date}
                                                </div>
                                            </>
                                        ),
                                    },
                                    {
                                        title: "Customer",
                                        field: "customer",
                                        headerStyle: { fontWeight: "bold", textAlign: "left" },
                                        cellStyle: { textAlign: "left", fontSize: 12 },
                                        render: (row) => (
                                            <>
                                                <div className="d-xl-none tc d-md-none d-lg-none">
                                                    Customer
                                                </div>
                                                <div>{row.customer}</div>
                                            </>
                                        ),
                                    },
                                    {
                                        title: "Fee Type",
                                        field: "fee_type",
                                        headerStyle: { fontWeight: "bold", textAlign: "left" },
                                        cellStyle: { textAlign: "left", fontSize: 12 },
                                        render: (row) => (
                                            <>
                                                <div className="d-xl-none tc d-md-none d-lg-none">
                                                    Fee Type
                                                </div>
                                                <div>{row.fee_type}</div>
                                            </>
                                        ),
                                    },
                                    {
                                        title: "Amount",
                                        field: "amount",
                                        headerStyle: { fontWeight: "bold", textAlign: "left" },
                                        cellStyle: { textAlign: "left", fontSize: 12 },
                                        render: (row) => (
                                            <>
                                                <div className="d-xl-none tc d-md-none d-lg-none">
                                                    Amount
                                                </div>
                                                <div>
                                                    {row.amount?.toLocaleString()}
                                                </div>
                                            </>
                                        ),
                                    },

                                    {
                                        title: t("Status"),
                                        field: "status",
                                        headerStyle: { fontWeight: "bold", textAlign: "left" },
                                        cellStyle: { textAlign: "left", fontSize: 12 },
                                        render: (row) => (
                                            <>
                                                <div className="d-xl-none tc d-md-none d-lg-none">
                                                    Status
                                                </div>
                                                <div>{row.status}</div>
                                            </>
                                        ),
                                    },
                                    {
                                        title: t("Approval"),
                                        field: "request",
                                        headerStyle: { fontWeight: "bold", textAlign: "left" },
                                        cellStyle: { textAlign: "left", fontSize: 12 },
                                        render: (row) => (
                                            <>
                                                <div className="d-xl-none tc d-md-none d-lg-none">
                                                    Approval
                                                </div>
                                                <div>{row.request}</div>
                                            </>
                                        ),
                                    },
                                ]}
                                data={filteredData}


                                actions={[
                                    (rowData) =>
                                        (
                                            (rowData.type === "Expense" && rowData.request === "Draft") ||
                                            (rowData.type === "Manual" && rowData.request === "Draft")
                                        ) && {
                                            icon: () => (
                                                <img
                                                    src={process.env.PUBLIC_URL + "/static/images/ic-edit.svg"}
                                                    alt="edit"
                                                />
                                            ),
                                            tooltip: "Edit",
                                            onClick: (event, rowData) => handleEdit(rowData),
                                        },

                                    (rowData) =>
                                        (
                                            (rowData.type === "Expense" && rowData.request === "Draft") ||
                                            (rowData.type === "Manual" && rowData.request === "Draft")
                                        ) && {
                                            icon: () => (
                                                <img
                                                    src={process.env.PUBLIC_URL + "/static/images/ic-delete.svg"}
                                                    alt="delete"
                                                />
                                            ),
                                            tooltip: "Delete",
                                            onClick: (event, rowData) => handleDelete(rowData),
                                        },
                                ]}

                                components={{
                                    Toolbar: (props) => (
                                        <div className="MuiToolbar-root MTableToolbar-spacer-21 my-temp-class">
                                            <MTableToolbar {...props} />
                                            <span
                                                onClick={() => setPaneState({ isPaneOpen: true })}
                                                style={{ cursor: "pointer", marginRight: 10 }}
                                            >
                                                <img src="/static/images/ic-filter.svg" alt="filter" />
                                            </span>

                                            <div className="dropdown">
                                                <button
                                                    className="btn btn-primary mb-0 dropdown-toggle"
                                                    data-bs-toggle="dropdown"
                                                    type="button"
                                                >
                                                    + {t("New Transaction")}
                                                </button>

                                                <ul
                                                    className="dropdown-menu"
                                                    style={{ padding: "15px" }}
                                                >
                                                    <li>
                                                        <span
                                                            onClick={() =>
                                                                navigate(
                                                                    "/expenses/add"
                                                                )
                                                            }
                                                        >
                                                            Expense
                                                        </span>
                                                    </li>

                                                    <li>
                                                        <span
                                                            onClick={() =>
                                                                navigate(
                                                                    "/addmanualaccounting"
                                                                )
                                                            }
                                                        >
                                                            Manual Accounting
                                                        </span>
                                                    </li>
                                                </ul>
                                            </div>
                                        </div>
                                    ),
                                }}

                                options={{
                                    headerStyle: {
                                        backgroundColor: "white",
                                        color: "#245C7F",
                                        fontSize: 12,
                                    },
                                    rowStyle: (rowData, index) => {
                                        if (index % 2) {
                                            return { backgroundColor: "#fff" };
                                        }
                                    },
                                    sorting: true,
                                    search: true,
                                    toolbar: true,
                                    paging: true,
                                    pageSize: 10,
                                    pageSizeOptions: [10, 20, 25, 50],
                                    emptyRowsWhenPaging: false,
                                    paginationType: "stepped",
                                    showFirstLastPageButtons: false,
                                    emptyRowsWhenPaging: false,
                                    actionsColumnIndex: -1,
                                    columnsButton: true,
                                }}

                                localization={{
                                    toolbar: {
                                        searchPlaceholder: t(
                                            "Search Transactions"
                                        ),
                                    },
                                }}
                            />
                        </div>
                        <SlidingPane
                            isOpen={paneState.isPaneOpen}
                            title="Filter By"
                            onRequestClose={handleClose}
                        >
                            <label>Transaction Type</label>

                            <select
                                className="form-control mb-3"
                                value={filterType}
                                onChange={(e) => setFilterType(e.target.value)}
                            >
                                <option value="">All</option>
                                <option value="Expense">Expense</option>
                                <option value="Manual">Manual Accounting</option>
                            </select>

                            <button
                                className="btn bg-gradient-secondary"
                                onClick={resetFilter}>
                                {t("Reset")}
                            </button>
                            &nbsp;&nbsp;
                            <button
                                className="btn btn-primary"
                                onClick={applyFilter}
                            >
                                {t("Apply")}
                            </button>
                        </SlidingPane>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default Transactions;
